import React, { Suspense, useEffect, useMemo, useState } from 'react';

import type {
	ExtensionHandler,
	ExtensionHandlers,
	ExtensionParams,
} from '@atlaskit/editor-common/extensions/extension-handler';
import type { ExtensionProvider } from '@atlaskit/editor-common/extensions/extension-provider';
import { getNodeRenderer } from '@atlaskit/editor-common/extensions/extension-handlers';
import { getExtensionRenderer } from '@atlaskit/editor-common/utils';

import { EXTENSION_TYPE } from '../extensionConstants.ts';

type MigrationStatus = 'pending' | 'migrated' | 'not-migrated';

type MigrationAwareRendererViewMacroProps = {
	node: ExtensionParams<any>;
	doc: object;
	extensionProvider: ExtensionProvider;
	renderRendererView: ExtensionHandler<any>;
};

// Forge extension type (EXTENSION_NAMESPACE in @atlaskit/xen-editor-provider). Kept local because
// EXTENSION_TYPE is coupled to content-renderer's SSR handler.
const FORGE_EXTENSION_TYPE = 'com.atlassian.ecosystem';

/**
 * Subset of the Forge manifest (ForgeExtensionManifest in @atlaskit/xen-editor-provider) used by
 * ForgeExtensionProvider to resolve a Connect/P2 macro key to its Forge successor.
 */
type MigrationManifest = {
	type?: string;
	key?: string;
	connectModuleKey?: string;
	dynamicConnectModuleKey?: string;
	migrationKey?: string;
	migratedFrom?: string[];
};

/**
 * A Connect macro migrated to Forge (h11n) is still stored as a `macro.core` node with its Connect
 * key. The Connect -> Forge swap only happens in the extension provider: ForgeExtensionProvider
 * resolves the Connect key to a Forge manifest. The macro is treated as migrated only when the
 * resolved manifest is a Forge manifest that claims the node's key by one of the provider's rules:
 * - `migrationKey` + `connectModuleKey` match (getConnectToForgeExtension)
 * - `migrationKey` + `dynamicConnectModuleKey` match (getDynamicConnectToForgeExtension)
 * - `migratedFrom` alias (findMigratedFromMatch)
 * - P2 -> Forge, where the Forge key equals the macro key (getP2ToForgeExtension)
 */
export const isMigratedManifest = (
	node: Pick<ExtensionParams<any>, 'extensionType' | 'extensionKey'>,
	manifest: MigrationManifest | undefined,
): boolean => {
	if (node.extensionType !== EXTENSION_TYPE.MACRO_CORE || manifest?.type !== FORGE_EXTENSION_TYPE) {
		return false;
	}

	const { extensionKey } = node;
	const hasMigrationKey = Boolean(manifest.migrationKey);

	return (
		(hasMigrationKey && manifest.connectModuleKey === extensionKey) ||
		(hasMigrationKey && manifest.dynamicConnectModuleKey === extensionKey) ||
		Boolean(manifest.migratedFrom?.includes(extensionKey)) ||
		manifest.key === extensionKey
	);
};

const MigrationAwareRendererViewMacro = ({
	node,
	doc,
	extensionProvider,
	renderRendererView,
}: MigrationAwareRendererViewMacroProps) => {
	const { extensionType, extensionKey } = node;
	const [status, setStatus] = useState<MigrationStatus>('pending');

	useEffect(() => {
		let cancelled = false;
		setStatus('pending');
		Promise.resolve()
			.then(() => extensionProvider.getExtension(extensionType, extensionKey))
			.then(
				(manifest) => {
					if (!cancelled) {
						setStatus(
							isMigratedManifest({ extensionType, extensionKey }, manifest)
								? 'migrated'
								: 'not-migrated',
						);
					}
				},
				() => {
					if (!cancelled) {
						setStatus('not-migrated');
					}
				},
			);
		return () => {
			cancelled = true;
		};
	}, [extensionProvider, extensionType, extensionKey]);

	const ProviderNodeRenderer = useMemo(
		() =>
			status === 'migrated'
				? getNodeRenderer(extensionProvider, extensionType, extensionKey)
				: undefined,
		[status, extensionProvider, extensionType, extensionKey],
	);

	if (status === 'pending') {
		// Avoid rendering the legacy Connect macro first and then swapping it for the Forge one
		return null;
	}

	if (ProviderNodeRenderer) {
		// Same path ExtensionComponent takes for non renderer-view extensions: the provider does the
		// Connect -> Forge (h11n) swap, matching the view page and the classic editor.
		return (
			<Suspense fallback={null}>
				<ProviderNodeRenderer node={node} showUnknownMacroPlaceholder />
			</Suspense>
		);
	}

	return <>{renderRendererView(node, doc)}</>;
};

/**
 * On live pages, 2P bodied macros are rendered in the "renderer view" with
 * `rendererExtensionHandlers`. ExtensionComponent uses that handler directly and never consults the
 * extension provider, so Connect macros migrated to Forge were always rendered via the legacy
 * Connect flow (and so was everything in their body).
 *
 * This wraps the `macro.core` renderer-view handler so migrated macros are rendered through the
 * extension provider instead. Non-migrated macros keep the existing renderer view.
 */
export const withMigratedMacroRendererView = (
	rendererExtensionHandlers: ExtensionHandlers | undefined,
	extensionProvider: ExtensionProvider | undefined,
): ExtensionHandlers | undefined => {
	const macroCoreHandler = rendererExtensionHandlers?.[EXTENSION_TYPE.MACRO_CORE];
	if (!rendererExtensionHandlers || !macroCoreHandler || !extensionProvider) {
		return rendererExtensionHandlers;
	}

	const renderRendererView = getExtensionRenderer(macroCoreHandler);

	const migrationAwareMacroCoreHandler: ExtensionHandler<any> = (node, doc) => (
		<MigrationAwareRendererViewMacro
			key={`${node.extensionType}:${node.extensionKey}:${node.localId ?? ''}`}
			node={node}
			doc={doc}
			extensionProvider={extensionProvider}
			renderRendererView={renderRendererView}
		/>
	);

	return {
		...rendererExtensionHandlers,
		[EXTENSION_TYPE.MACRO_CORE]: migrationAwareMacroCoreHandler,
	};
};
